import React from 'react';
import styles from '../../../../styles/PerformanceDashboard.module.css';
import { UserPerformanceData } from '../types';

interface UserInfoCardProps {
  user: {
    full_name: string;
    email: string;
    role: string;
    designation?: string;
    joined_at?: string;
    avatar?: string;
  };
  formatDate: (dateString: string) => string;
  statistics: UserPerformanceData['statistics'];
  badgesCount?: number;
}

const UserInfoCard: React.FC<UserInfoCardProps> = ({ user, formatDate, statistics, badgesCount = 0 }) => {
  // Format designation for display
  const formatDesignation = (designation?: string) => {
    if (!designation) return 'N/A';
    return designation
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className={styles.userInfoCard}>
      <div className={styles.userAvatar}>
        {user.avatar ? (
          <img src={user.avatar} alt={user.full_name} />
        ) : (
          <div className={styles.avatarPlaceholder}>
            {user.full_name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className={styles.userDetails}>
        <h2>{user.full_name}</h2>
        <p>{user.email}</p>
        <div className={styles.userMeta}>
          <span className={styles.userDesignation}>
            {formatDesignation(user.designation)}
          </span>
          {user.joined_at && (
            <span className={styles.userJoined}>Joined {formatDate(user.joined_at)}</span>
          )}
        </div>
        
        {/* Summary Stats */}
        <div className={styles.summaryStats}>
          <div className={styles.summaryStatItem}>
            <div className={styles.summaryStatIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4"></path>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
              </svg>
            </div>
            <div className={styles.summaryStatContent}>
              <span className={styles.summaryStatValue}>{statistics.tasks.completed}</span>
              <span className={styles.summaryStatLabel}>Tasks Completed</span>
            </div>
          </div>
          
          <div className={styles.summaryStatItem}>
            <div className={styles.summaryStatIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
              </svg>
            </div>
            <div className={styles.summaryStatContent}>
              <span className={styles.summaryStatValue}>{statistics.points.total.toLocaleString()}</span>
              <span className={styles.summaryStatLabel}>Total Points</span>
            </div>
          </div>
          
          <div className={styles.summaryStatItem}>
            <div className={styles.summaryStatIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="7"></circle>
                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
              </svg>
            </div>
            <div className={styles.summaryStatContent}>
              <span className={styles.summaryStatValue}>{badgesCount}</span>
              <span className={styles.summaryStatLabel}>Badges Earned</span>
            </div>
          </div>
          
          <div className={styles.summaryStatItem}>
            <div className={styles.summaryStatIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className={styles.summaryStatContent}>
              <span className={styles.summaryStatValue}>{statistics.tasks.completion_rate.toFixed(1)}%</span>
              <span className={styles.summaryStatLabel}>Completion Rate</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInfoCard;

